import { Edit } from "@mui/icons-material";
import {
  Stack,
  Button,
  Box,
  LinearProgress,
  Typography,
  IconButton,
  Checkbox,
  Alert,
  Snackbar,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Column, IntakeQuestion } from "src/api/types";
import { theme } from "src/assets/theme";
import HoverReveal from "src/components/HoverReveal";
import AddNewIntakeQuestion from "src/components/Modals/AddNewIntakeQuestion";
import TruncatedText from "src/components/TruncatedText";
import { MatchParam } from "src/constants/matches";
import { useIntakeDrawer } from "src/context/drawers/IntakesDrawerProvider/useIntakesDrawer";
import { useManageModal } from "src/hooks/useManageModal";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SortableDataTable from "src/components/SortableDataTable";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/api/store";
import {
  intakeQuestionsApi,
  useBulkUpdateQuestionsMutation,
  useCopyQuestionsMutation,
  useDeleteQuestionsMutation,
  useGetIntakeQuestionsQuery,
} from "src/api/services/intakeQuestionsApi";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import Actions from "./components/Actions";
import CopyItemModal from "src/components/Modals/CopyItemModal";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import RemoveItemModal from "src/components/Modals/RemoveItemModal";

const useColumns = (): Column<IntakeQuestion>[] => {
  const { handleOpenUpdateIntakeQuestion } = useIntakeDrawer();
  const { selected, setSelected } = useSelection();

  const handleSelect = useCallback(
    (id: number) => {
      setSelected(
        selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id],
      );
    },
    [selected, setSelected],
  );

  const isSelected = useCallback(
    (id: number) => selected.includes(id),
    [selected],
  );

  return [
    {
      id: "checkbox",
      label: "Select",
      minWidth: 50,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => (
        <Checkbox
          color="primary"
          aria-label="select task"
          onChange={() => handleSelect(row.id)}
          checked={isSelected(row.id)}
        />
      ),
    },
    {
      id: "drag",
      label: "",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: () => <DragIndicatorIcon color="info" />,
    },
    {
      id: "edit",
      label: "Edit",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => (
        <IconButton onClick={() => handleOpenUpdateIntakeQuestion(row)}>
          <Edit sx={{ color: theme.palette.secondary.main }} />
        </IconButton>
      ),
    },
    {
      id: "id",
      label: "ID",
      orderable: false,
      align: "center",
      format: (row) => row.id.toString(),
      render: (row) => <Link to={row.id.toString()}>{row.id}</Link>,
    },
    {
      id: "slug",
      label: "Slug",
      orderable: false,
      align: "center",
      format: (row) => row.slug,
      render: (row) => <Typography>{row.slug}</Typography>,
    },
    {
      id: "position",
      label: "Position",
      orderable: false,
      align: "center",
      format: (row) => row.question_position?.toString() ?? "N/A",
      render: (row) => (
        <Typography>{row.question_position ?? "N/A"}</Typography>
      ),
    },
    {
      id: "title",
      label: "Title",
      orderable: false,
      minWidth: 200,
      align: "center",
      format: (row) => row.title,
      render: (row) => <TruncatedText text={row.title} maxLength={20} />,
    },
    {
      id: "subtitle",
      label: "Subtitle",
      orderable: false,
      minWidth: 200,
      align: "center",
      format: (row) => row.subtitle || "",
      render: (row) => (
        <TruncatedText text={row.subtitle || "N/A"} maxLength={20} />
      ),
    },
    {
      id: "required",
      label: "Required",
      orderable: false,
      align: "center",
      format: (row) => row.required_answer.toString(),
      render: (row) => (
        <Typography>{row.required_answer ? "✔️" : ""}</Typography>
      ),
    },
    {
      id: "follow_up",
      label: "Follow Up",
      orderable: false,
      align: "center",
      format: (row) => row.follow_up.toString(),
      render: (row) => <Typography>{row.follow_up ? "✔️" : ""}</Typography>,
    },
    {
      id: "type",
      label: "Type",
      orderable: false,
      minWidth: 100,
      align: "center",
      format: (row) => row.question_type,
      render: (row) => (
        <Typography textTransform="capitalize">{row.question_type}</Typography>
      ),
    },
    {
      id: "tag",
      label: "Tag",
      orderable: false,
      minWidth: 100,
      align: "center",
      format: (row) => row.tag || "",
      render: (row) => <Typography>{row.tag || "N/A"}</Typography>,
    },
    {
      id: "next",
      label: "Next",
      orderable: false,
      minWidth: 100,
      align: "center",
      format: (row) => row.next_question?.slug ?? "N/A",
      render: (row) => {
        if (row.next_question == null) {
          return <Typography>N/A</Typography>;
        }

        return (
          <HoverReveal
            label={row.next_question.slug}
            value={row.next_question.title}
          />
        );
      },
    },
  ];
};

const IntakeQuestions = () => {
  const [open, { handleClose, handleOpen }] = useManageModal();
  const [
    openCopy,
    { handleClose: handleCloseCopy, handleOpen: handleOpenCopy },
  ] = useManageModal();
  const [
    openRemove,
    { handleClose: handleCloseRemove, handleOpen: handleOpenRemove },
  ] = useManageModal();
  const { intakeId } = useParams<MatchParam<"INTAKE_ID">>();
  const { data: questions = [], isFetching } = useGetIntakeQuestionsQuery(
    intakeId != null ? Number(intakeId) : skipToken,
  );
  const [updateQuestions] = useBulkUpdateQuestionsMutation();
  const [deleteQuestions, { isLoading }] = useDeleteQuestionsMutation();
  const columns = useColumns();
  const height = useMemo(() => window.innerHeight - 104 - 64 - 34, []);
  const dispatch = useDispatch<AppDispatch>();
  const { selected } = useSelection();
  const [copyQuestions] = useCopyQuestionsMutation();
  const errLog = useSentryLogger("error");
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<string | null>(null);

  const handleReorder = useCallback(
    (reorderedQuestions: IntakeQuestion[]) => {
      dispatch(
        intakeQuestionsApi.util.updateQueryData(
          "getIntakeQuestions",
          Number(intakeId),
          (draft) => {
            const reordered = reorderedQuestions.map(
              (q) => draft.find((d) => d.id === q.id)!,
            );
            draft.splice(0, draft.length, ...reordered);
          },
        ),
      );

      updateQuestions({
        intakeId: Number(intakeId),
        body: reorderedQuestions.map((q, idx) => ({
          id: q.id,
          question_position: idx,
          question_id: q.question_id,
        })),
      });
    },
    [dispatch, intakeId, updateQuestions],
  );

  const handleCopy = useCallback(
    async (selectedIntake: number) => {
      setLoading(true);

      try {
        const copy = await copyQuestions({
          receivingIntakeId: selectedIntake,
          extractedIntakeId: Number(intakeId),
          selectedIds: selected,
        }).unwrap();

        setAlert(copy.message);
      } catch (err) {
        errLog("Unable to copy questions", err);
      } finally {
        setLoading(false);
      }
    },
    [copyQuestions, errLog, intakeId, selected],
  );

  const handleDelete = useCallback(async () => {
    try {
      await deleteQuestions({ selectedIds: selected }).unwrap();

      setAlert(
        `Successfully removed ${selected.length} questions from intake ${intakeId}`,
      );
    } catch (error) {
      errLog("Could not delete questions", error);
    }
  }, [deleteQuestions, errLog, intakeId, selected]);

  return (
    <Stack gap={2} sx={{ width: "100%", minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">Questions</Typography>
        <Button onClick={handleOpen}>+ Add Question</Button>
      </Stack>
      {selected.length > 0 ? (
        <Actions
          count={selected.length}
          handleOpen={handleOpenCopy}
          questions={questions}
          handleOpenRemove={handleOpenRemove}
        />
      ) : null}
      <Stack gap={0} sx={{ width: "100%" }}>
        <Box height={4}>{isFetching ? <LinearProgress /> : null}</Box>
        <SortableDataTable
          data={questions}
          columns={columns}
          height={height}
          primaryKey="id"
          onReorder={handleReorder}
        />
      </Stack>
      <Snackbar
        open={!!alert}
        autoHideDuration={3000}
        onClose={() => setAlert(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled">
          {alert}
        </Alert>
      </Snackbar>
      {intakeId ? (
        <AddNewIntakeQuestion
          open={open}
          onClose={handleClose}
          intakeId={Number(intakeId)}
        />
      ) : null}
      {openCopy ? (
        <CopyItemModal
          onClose={handleCloseCopy}
          open={openCopy}
          component="Intake Questions"
          table="intake_questions_v2"
          onSubmit={handleCopy}
          loading={loading}
        />
      ) : null}
      {openRemove ? (
        <RemoveItemModal
          open={openRemove}
          onClose={handleCloseRemove}
          onDelete={handleDelete}
          loading={isLoading}
        />
      ) : null}
    </Stack>
  );
};

export default IntakeQuestions;
